import { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../hooks/useAuth';
import { timeTrackingService, UpdateTimeEntryInput } from '../services/timeTracking';
import { useProjects } from '../hooks/useProjects';
import { useUserSettingsContext } from '../hooks/useUserSettingsContext';
import CustomPagination from '../components/CustomPagination';
import TimeEntryForm from '../components/TimeEntryForm';
import ConfirmationDialog from '../components/ConfirmationDialog';
import CustomDropdown from '../components/CustomDropdown';
import { useAdminEntries, SortField, TimeEntryWithUser } from '../hooks/useAdminEntries';

export default function EntryManagement() {
  const { user } = useAuth();
  const projects = useProjects({ loadInactive: true });
  const { effectiveWeekStartDay } = useUserSettingsContext();

  // IDs of archived projects — their entries are hidden in admin view too
  const archivedProjectIds = useMemo(
    () => new Set((projects.allProjects || []).filter(p => p.status === 'archived').map(p => p.id)),
    [projects.allProjects]
  );

  // Only active projects — used for the edit form's project picker
  const activeProjects = useMemo(
    () => (projects.allProjects || []).filter(p => p.status === 'active' || !p.status),
    [projects.allProjects]
  );

  const {
    entries,
    users,
    loading,
    error,
    pagination,
    sortField,
    sortOrder,
    selectedUserId,
    handleUserChange,
    handlePageChange,
    handlePageSizeChange,
    handleSort,
    getCurrentDuration,
    deleteEntry,
    refresh,
  } = useAdminEntries();

  // Filter out entries belonging to archived projects
  const filteredAdminEntries = useMemo(
    () => entries.filter(entry => !entry.projectId || !archivedProjectIds.has(entry.projectId)),
    [entries, archivedProjectIds]
  );

  const [editingEntry, setEditingEntry] = useState<TimeEntryWithUser | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    entryId: string | null;
  }>({
    isOpen: false,
    entryId: null,
  });

  const handleEdit = (entry: TimeEntryWithUser) => {
    setEditingEntry({
      ...entry,
      isRunning: !entry.endTime
    } as TimeEntryWithUser & { isRunning: boolean });
  };

  const handleUpdate = async (updates: UpdateTimeEntryInput) => {
    if (editingEntry) {
      try {
        // Pass adminUserId parameter for Entry Management context
        const adminUserId = selectedUserId === "" ? "all" : selectedUserId;
        await timeTrackingService.updateTimeEntry(editingEntry.id, updates, adminUserId);
        setEditingEntry(null);
        // Refresh the entries to show updated data
        refresh();
      } catch (err) {
        console.error('Failed to update time entry:', err);
      }
    }
  };

  const handleStopTimeEntry = async (entry: TimeEntryWithUser) => {
    try {
      // Pass adminUserId parameter for Entry Management context
      const adminUserId = selectedUserId === "" ? "all" : selectedUserId;
      await timeTrackingService.stopTimeEntry(entry.id, adminUserId);
      // Refresh the entries to show updated data
      refresh();
    } catch (err) {
      console.error('Failed to stop time entry:', err);
    }
  };

  const handleEntryClick = (entry: TimeEntryWithUser) => {
    // Readers can only view, not edit
    if (user?.role === 'reader') {
      return;
    }
    handleEdit(entry);
  };

  const handleDelete = async (entryId: string) => {
    setDeleteDialog({
      isOpen: true,
      entryId,
    });
  };

  const handleConfirmDelete = async () => {
    if (deleteDialog.entryId) {
      try {
        await deleteEntry(deleteDialog.entryId);
      } catch {
        // Error is handled by the hook
      } finally {
        setDeleteDialog({ isOpen: false, entryId: null });
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialog({ isOpen: false, entryId: null });
  };

  const onSortChange = (field: SortField) => {
    handleSort(field);
  };

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <button
      onClick={() => onSortChange(field)}
      className="flex items-center space-x-1 text-left font-medium text-gray-900 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded px-2 py-1"
    >
      <span>{children}</span>
      {sortField === field && (
        <span className="text-gray-400">
          {sortOrder === 'asc' ? '↑' : '↓'}
        </span>
      )}
    </button>
  );

  const formatDuration = (seconds?: number): string => {
    if (!seconds) return '0s';
    return timeTrackingService.formatDuration(seconds);
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString([], {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (dateString: string): string => {
    return new Date(dateString).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  if (!user || (user.role !== 'admin' && user.role !== 'reader')) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Access Denied
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>You don't have permission to access this page. Only administrators and readers can view time entries.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters and Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">Filter by User:</span>
              <CustomDropdown
                id="user-filter"
                label=""
                placeholder="Select User"
                options={[
                  { value: '', label: 'All' },
                  ...users.map((userItem) => ({
                    value: userItem.id,
                    label: `${userItem.name} (${userItem.email})`
                  }))
                ]}
                selectedValues={selectedUserId ? [selectedUserId] : ['']}
                onSelectionChange={(values) => handleUserChange(values[0] || '')}
                multiSelect={false}
                minWidth="min-w-[400px]"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <CustomPagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              totalCount={pagination.totalCount}
              hasNextPage={pagination.hasNextPage}
              hasPrevPage={pagination.hasPrevPage}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSize={pagination.pageSize}
              className="mb-0"
              transparency="medium"
              minWidth="min-w-[400px]"
            />
          </div>
        </div>
      </div>



      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && !entries.length ? (
        <div className="flex justify-center items-center py-12">
          <div data-testid="loading-spinner" className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {/* Table Header */}
          <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
            <div className="grid grid-cols-12 items-center">
              <div className="col-span-2">
                <SortableHeader field="userName">
                  <span className="text-sm font-normal text-gray-900">User</span>
                </SortableHeader>
              </div>
              <div className="col-span-2">
                <SortableHeader field="description">
                  <span className="text-sm font-normal text-gray-900">Description</span>
                </SortableHeader>
              </div>
              <div className="col-span-1">
                <SortableHeader field="location">
                  <span className="text-sm font-normal text-gray-900">Location</span>
                </SortableHeader>
              </div>
              <div className="col-span-2">
                <SortableHeader field="startTime">
                  <span className="text-sm font-normal text-gray-900">Start Time</span>
                </SortableHeader>
              </div>
              <div className="col-span-2">
                <SortableHeader field="endTime">
                  <span className="text-sm font-normal text-gray-900">End Time</span>
                </SortableHeader>
              </div>
              <div className="col-span-1">
                <SortableHeader field="duration">
                  <span className="text-sm font-normal text-gray-900">Duration</span>
                </SortableHeader>
              </div>
              <div className="col-span-2">
                <span className="text-sm font-normal text-gray-900">Status</span>
              </div>
            </div>
          </div>

          {/* Table Body */}
          <ul className="divide-y divide-gray-200">
            {filteredAdminEntries.map((entry) => (
              <li 
                key={entry.id} 
                className={`px-6 py-4 hover:bg-gray-50 ${
                  user?.role === 'reader' ? 'cursor-default' : 'cursor-pointer'
                }`}
                onClick={() => handleEntryClick(entry)}
              >
                <div className="grid grid-cols-12 items-center">
                  <div className="col-span-2">
                    <div className="text-sm text-gray-900 font-medium">{entry.userName}</div>
                    <div className="text-xs text-gray-500">{entry.userEmail}</div>
                  </div>
                  <div className="col-span-2 text-xs text-gray-500">
                    <div className="line-clamp-2">{entry.description}</div>
                  </div>
                  <div className="col-span-1 text-xs text-gray-500">
                    {entry.location ? (
                      <div className="flex items-center space-x-1">
                        <svg className="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                        <span className="truncate">{entry.location}</span>
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </div>
                  <div className="col-span-2 text-xs text-gray-500">
                    <div>{formatDate(entry.startTime)}</div>
                    <div>{formatTime(entry.startTime)}</div>
                  </div>
                  <div className="col-span-2 text-xs text-gray-500">
                    {entry.endTime ? (
                      <>
                        <div>{formatDate(entry.endTime)}</div>
                        <div>{formatTime(entry.endTime)}</div>
                      </>
                    ) : (
                      <span className="text-yellow-600">Running</span>
                    )}
                  </div>
                  <div className="col-span-1 text-sm font-medium text-gray-900">
                    {formatDuration(entry.endTime ? entry.duration : getCurrentDuration(entry))}
                  </div>
                  <div className="col-span-2 relative">
                    <div className="flex items-center">
                      <span className={`inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-medium w-20 ${
                        entry.endTime 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {entry.endTime ? 'Completed' : 'Running'}
                      </span>
                    </div>
                    <div className="absolute right-8 top-1/2 transform -translate-y-1/2 flex items-center space-x-2">
                      {!entry.endTime && user?.role !== 'reader' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStopTimeEntry(entry);
                          }}
                          className="text-red-600 hover:text-red-800 focus:outline-none focus:text-red-800"
                          title="Stop time tracking"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                          </svg>
                        </button>
                      )}
                      {user?.role === 'admin' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(entry.id);
                          }}
                          className="text-gray-400 hover:text-red-500 focus:outline-none focus:text-red-500"
                          title="Delete entry"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>

          {/* Empty State */}
          {!loading && entries.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-500">
                {selectedUserId ? 'No time entries found for the selected user.' : 'No time entries found.'}
              </div>
            </div>
          )}
        </div>
      )}

      {editingEntry && user?.role !== 'reader' && createPortal(
        <TimeEntryForm
          mode="edit"
          entry={editingEntry as TimeEntryWithUser & { isRunning: boolean }}
          projects={activeProjects}
          onSubmit={handleUpdate}
          onCancel={() => setEditingEntry(null)}
          loading={loading}
          weekStartsOn={effectiveWeekStartDay}
        />,
        document.body
      )}

      <ConfirmationDialog
        isOpen={deleteDialog.isOpen}
        title="Delete Time Entry"
        message="Are you sure you want to delete this time entry? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        loading={loading}
      />

    </div>
  );
} 